import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useRegisterSW } from 'virtual:pwa-register/react';
import {
  Bell,
  Layers,
  ListChecks,
  Menu,
  NotebookPen,
  PenLine,
  Feather,
  PenTool,
  RefreshCw,
  Search,
  Settings,
  Sunrise,
  UserRound,
  WifiOff,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';


import { api } from '../lib/api';
import {
  dayKey,
  formatDayHeading,
  formatGregorianFull,
  formatHijriDisplay,
  greeting,
} from '../lib/dates';
import { useActivityStreamStatus } from './stream-provider';
import { useAgendaQuery } from '../features/today/queries';
import { liveLabel } from './live-label';
import { BrandName } from './BrandName';
import type { Page, VentureDto } from '../lib/dto';
import { qk } from '../lib/query-keys';
import { useShortcuts, type Shortcut } from '../lib/shortcuts';
import { UserSpan } from '../components/UserText';
import { ventureColorVar } from '../features/projects/venture-color';
import { CONTENT_WIDTH_CLASS, contentWidthForPath } from './layout-width';
import { FocusModeContext, focusModeKeyAction, type FocusModeValue } from './focus-mode';
import { isNavActive } from './nav-active';

/**
 * AppShell (§1.6 و§5.5 و§6.10): قائمة جانبية ثابتة على الحافة البادئة (يمين تحت
 * RTL) تُطوى بزر صريح وتحفظ اختيار المالك. وعلى الجوال يتبع النموذج نفسه: زرّ
 * القائمة في الرأس يفتح درجًا من اليمين بدل شريطٍ سفلي يغيّر بنية التنقّل.
 * المحتوى والقائمة يملكان تمريرًا مستقلًا كـBestVersion.
 *
 * قاعدة ملزمة في كل هذا الملف: خصائص منطقية حصرًا (ps/pe/ms/me/start/end/border-s/e).
 * أي `left/right` يفشل `pnpm lint:rtl` — والقلب ليس تجميلًا بل بنية (§3.1).
 */

interface NavItem {
  readonly to:
    | '/'
    | '/tasks'
    | '/projects'
    | '/reminders'
    | '/personal/notebook'
    | '/personal/workshop'
    | '/personal/poems'
    | '/personal'
    | '/search'
    | '/settings';
  readonly label: string;
  readonly icon: LucideIcon;
  readonly exact?: boolean;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/', label: 'اليوم', icon: Sunrise, exact: true },
  /*
   * «المهام» — صفحةٌ مستقلّة بأمر المالك 2026-08-11، وسؤالُه كان تشخيصًا لا
   * استفسارًا: «هل توجد صفحة مستقلة للمهام؟ هل الصندوق هي صفحة المهام؟» ثم
   * «أجل وين صفحة المهام؟ هل هي صفحة (بانتظار)؟».
   *
   * ولم تكن أيًّا منهما: `/inbox` **التقاطاتٌ خام** لم تصر مهامَّ بعد،
   * و`/waiting` شريحة `status='waiting'` وحدها. فكانت المهامّ تُرى في ثلاثة
   * مواضع جزئية ولا موضع يجمعها.
   *
   * **وصفحة «بانتظار» حُذفت 2026-10-13** بقراره: الشريحة نفسها **تبويبٌ قائم**
   * في هذه الصفحة (`TasksScreen`)، وقِيس أن صفر مهمّة من 92 في تلك الحالة
   * وانتقالًا واحدًا في عمر النظام — فكانت صفحةً فارغةً تسعة أسابيع ونسخةً من
   * تبويبٍ على بُعد ضغطة.
   *
   * **ثم حُذف «الصندوق» في اليوم نفسه** (‏19 صفًّا، وصفرُ حدث في 48 يومًا)، وصار
   * القيد السريع (‏`Shift+Q`) يكتب **مهمّةً هنا** بلا فرزٍ وسيط. فصارت هذه
   * الصفحة ما سأل عنه: الموضع الواحد الذي تُرى فيه المهامّ كلها.
   */
  { to: '/tasks', label: 'المهام', icon: ListChecks },
  { to: '/projects', label: 'المشاريع', icon: Layers },
  { to: '/reminders', label: 'التذكيرات', icon: Bell },
  /*
   * **وجهتان مرفوعتان من «شخصي» إلى القائمة** بطلب المالك: «المفكرة» (‏2026-10-14)
   * ثم «ورشة الكتابة» (‏2026-10-16: «هذي الزرين خليها بالقائمة الجانبية، فوق
   * كلمة شخصي، واحذفها من تبويب شخصي»).
   *
   * وهما **مسارانِ فرعيّان** في جدولٍ بقيّتُه جذور — وذلك مقصود: ما يُفتح كلَّ
   * يوم يُفتح بضغطة، لا بضغطتين عبر صفحة الوجهات. **وبطاقتاهما حُذفتا من
   * `PersonalHub`** في الالتزام نفسه: بابٌ في موضعين يجعل أحدهما يتقادم.
   *
   * ولا `exact` عليهما: الفرعُ أعمق من أبيه فيبقى نشطًا داخل تبويباته،
   * و«شخصي» لا يُضيء معهما — يحسم ذلك `isNavActive` بقاعدة «الأخصّ يفوز».
   */
  { to: '/personal/notebook', label: 'المفكرة', icon: NotebookPen },
  { to: '/personal/workshop', label: 'ورشة الكتابة', icon: PenTool },
  /* و«القصائد» ثالثةً بطلبه 2026-10-20: «أريد استحداث قسم جديد لكتابة القصائد». */
  { to: '/personal/poems', label: 'القصائد', icon: Feather },
  /* «شخصي» — ما ليس مشروعًا ولا مهمة (خطة القسم الشخصي ش1): الأفكار والأخبار
     والأرشيف والمواعيد والإنجليزية والمتابعة اليومية ورادار وPMP. */
  { to: '/personal', label: 'شخصي', icon: UserRound },
  /* لا «البحث» هنا بطلب المالك: أيقونته في الشريط العلوي تكفي، وتكراره في مسار
     العمل يضيف صفًّا لا يضيف طريقًا. المسار `/search` نفسه باقٍ ويُفتح منها. */
  { to: '/settings', label: 'الإعدادات', icon: Settings },
];

/**
 * لوحة الـ 12 لونًا (§5.3) — **تُستورَد ولا تُنسَخ** (بند N-12): كانت مكتوبة هنا وفي
 * `features/projects/project-options.ts` وفي `features/projects/venture-color.ts`،
 * ثلاث نسخ متطابقة اليوم ولا شيء يمنع انحرافها غدًا.
 */
const ventureColor = ventureColorVar;

/**
 * اختصار الالتقاط `Shift+Q` بطلب المالك: مفتاحاه متجاوران تحت اليد اليسرى، بخلاف
 * `Ctrl+K` الذي يفرّق الإصبعين عبر الصفّين. وواحد لا اثنان — لا فرق بين المنصّات
 * (لا `⌘` ولا `Ctrl`) فالنص المعروض والاختصار الفعلي شيء واحد.
 *
 * وسلامته مضمونة بقاعدة قائمة في `useShortcuts`: الاختصار **بلا مُعدِّل Ctrl/⌘**
 * لا يُلتقط داخل حقل كتابة — فكتابة حرف Q كبيرًا في صندوق الالتقاط تبقى كتابة.
 */
const CAPTURE_SHORTCUT_LABEL = 'Shift+Q';
const SIDEBAR_COLLAPSED_KEY = 'flow-sidebar-collapsed';

function readSidebarCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function persistSidebarCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // التخزين المحلي تحسين راحة لا شرط تشغيل؛ الواجهة تبقى قابلة للطي في الجلسة.
  }
}

/**
 * سطح فتح القيد السريع. `features/quick-add` يغلّف الشجرة في `__root.tsx` بمزوّد
 * يفتح اللوحة **فوق** الشاشة الحالية، فهو الموجود دائمًا.
 *
 * وكان التنفيذ الافتراضي هنا ينتقل إلى شاشة `/capture` الكاملة — وقد حُذفت مع
 * «الصندوق» (N-368). فالافتراضي اليوم **لا شيء**: استدعاءٌ بلا مزوّد لا يفتح
 * شيئًا ولا يُرسل المالك إلى مسارٍ لا وجود له.
 */
export interface CaptureTriggerValue {
  readonly openCapture: () => void;
}

export const CaptureTriggerContext = createContext<CaptureTriggerValue | null>(null);

export function useCaptureTrigger(): CaptureTriggerValue {
  const value = useContext(CaptureTriggerContext);
  if (value === null) throw new Error('useCaptureTrigger خارج AppShell');
  return value;
}

export function AppShell({ children }: { children: ReactNode }) {
  const online = useOnlineStatus();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const contentWidth = contentWidthForPath(pathname);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuTriggerRef = useRef<HTMLButtonElement>(null);
  /** وضع التركيز (‏`focus-mode.ts`) — تطلبه الشاشة، والقشرة تتنحّى. */
  const [focusMode, setFocusMode] = useState(false);

  const toggleSidebar = (): void => {
    setSidebarCollapsed((current) => {
      const next = !current;
      persistSidebarCollapsed(next);
      return next;
    });
  };

  const closeMobileMenu = useCallback((): void => {
    setMobileMenuOpen(false);
    requestAnimationFrame(() => mobileMenuTriggerRef.current?.focus());
  }, []);

  /* التنقّل من رابطٍ آخر يغلق الدرج أيضًا؛ فلا يبقى modal فوق الصفحة الجديدة.
     ووضع التركيز كذلك: صفحةٌ أخرى بلا قشرةٍ ولا زرِّ خروجٍ منها. */
  useEffect(() => {
    setMobileMenuOpen(false);
    setFocusMode(false);
  }, [pathname]);

  /*
   * Escape يُخرج من وضع التركيز أينما كان التركيز — في المتن أو العنوان أو لا
   * شيء. والقرار واستثناءاته دالّةٌ نقيّة مختبَرة (‏`focusModeKeyAction`).
   *
   * **وفي طور الالتقاط لا الفقاعة** — وهذا مقيسٌ في متصفح لا مُختار: المحرّر
   * (‏ProseMirror) يستهلك Escape ويستدعي `preventDefault`، فيصل الحدث إلى
   * `window` وقد صار `defaultPrevented` — فلا يخرج المالك أبدًا وهو يكتب في
   * المتن، وهو الموضع الوحيد الذي يكون فيه التركيز عمليًّا.
   */
  useEffect(() => {
    if (!focusMode) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      /* طبقةٌ فوق الصفحة تملك Escape: تُغلق هي، ولا تُسحب الصفحة البيضاء من تحتها. */
      const dialogOpen =
        document.querySelector(
          'dialog[open], [data-state="open"][role="dialog"], [data-state="open"][role="menu"], [data-state="open"][role="listbox"]',
        ) !== null;
      if (focusModeKeyAction(event, dialogOpen) === 'exit') setFocusMode(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [focusMode]);

  const focus = useMemo<FocusModeValue>(
    () => ({ active: focusMode, setActive: setFocusMode }),
    [focusMode],
  );

  /**
   * مزوّد أعلى في الشجرة (`CaptureSurface` في `features/quick-add`) يفتح اللوحة
   * **فوق** الشاشة الحالية — وبه يعمل زرّ الشريط الجانبي والزرّ السفلي
   * والاختصاران معًا. وغيابُه حالةُ اختبارٍ لا حالةُ إنتاج.
   */
  const override = useContext(CaptureTriggerContext);
  const capture = useMemo<CaptureTriggerValue>(
    () => override ?? { openCapture: () => undefined },
    [override],
  );

  // §6.4 و§8: بالكود الفيزيائي — يعمل في التخطيطين العربي والإنجليزي.
  const shortcuts = useMemo<readonly Shortcut[]>(
    () => [
      { code: 'KeyQ', withShift: true, handler: capture.openCapture },
      { code: 'KeyC', handler: capture.openCapture },
    ],
    [capture],
  );
  useShortcuts(shortcuts);

  return (
    <CaptureTriggerContext.Provider value={capture}>
      <div className="app-canvas flex min-h-dvh text-text lg:h-dvh lg:overflow-hidden">
        {/*
         * رابط التخطّي (WCAG 2.4.1 «تجاوز الكتل»، المستوى A — بند R-17): الرمز
         * `--z-skiplink` كان مُعلَنًا في `tokens.css` **بلا مقابل في الشجرة** —
         * وتعليقه نفسه يذمّ «سلّمًا مُعلَنًا وغير مُنفَّذ». فمن يتنقّل بلوحة المفاتيح
         * كان يمرّ على الشريط الجانبي كله في كل شاشة قبل أن يبلغ المحتوى.
         *
         * مخفيٌّ حتى يناله التركيز: `sr-only` ثم `focus:not-sr-only`. فلا يزيد سطرًا
         * على المسار البصري اليومي — وهو شرط P-1 لما يُضاف صامتًا.
         */}
        <a
          href="#main"
          className="sr-only z-[var(--z-skiplink)] focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2 focus:text-body focus:text-text focus:shadow-lg"
        >
          تخطَّ إلى المحتوى
        </a>
        {/*
         * وضع التركيز يُزيل الكروم من التدفّق لا يغطّيه (‏`focus-mode.ts`): الإخوة
         * يحفظون مواضعهم بـ`null`، فـ`main` وما فيه لا يُفكّ ولا يُركَّب.
         */}
        {focusMode ? null : <SideNav collapsed={sidebarCollapsed} onToggle={toggleSidebar} />}
        <div className="flex min-w-0 flex-1 flex-col lg:h-dvh lg:overflow-hidden">
          {focusMode ? null : (
            <TopBar
              online={online}
              isToday={pathname === '/'}
              onCapture={capture.openCapture}
              onOpenMenu={() => setMobileMenuOpen(true)}
              menuOpen={mobileMenuOpen}
              menuTriggerRef={mobileMenuTriggerRef}
            />
          )}
          {/* شريطا الاتصال والتحديث يعودان مع الخروج؛ وتعذّرُ الحفظ نفسه يقوله المحرّر. */}
          {!online && !focusMode && <OfflineBar />}
          {focusMode ? null : <UpdateBar />}
          {/* بلا صنف حركة دخول: التنقل بين الصفحات بلا انتقال (DESIGN.md §Motion). */}
          <main
            id="main"
            tabIndex={-1}
            /* الحشو الأفقيّ `lg:px-6` (‏24px) لا `px-8` — مقيسٌ من المرجع:
               `main … px-3 py-4 lg:px-6 lg:py-8`. وثمانية بكسلات في كل جانب
               تُضيّق أعرض الشاشات بستّة عشر بلا مقابل. */
            className="w-full flex-1 overflow-visible px-4 pt-5 pb-[calc(32px+env(safe-area-inset-bottom))] outline-none sm:pt-6 lg:min-h-0 lg:overflow-y-auto lg:px-6 lg:pt-8 lg:pb-12"
          >
            <div className={'mx-auto w-full ' + CONTENT_WIDTH_CLASS[contentWidth]}>
              <FocusModeContext.Provider value={focus}>{children}</FocusModeContext.Provider>
            </div>
          </main>
        </div>
        <MobileNavDrawer
          open={mobileMenuOpen}
          onClose={closeMobileMenu}
          onCapture={() => {
            setMobileMenuOpen(false);
            requestAnimationFrame(() => capture.openCapture());
          }}
        />
      </div>
    </CaptureTriggerContext.Provider>
  );
}

/* ==================== الديسكتوب: Sidebar على الحافة البادئة ==================== */

/**
 * ألوان الرابط الخامل تعيش في `inactiveProps` لا في الصنف المشترك — وبذلك لا
 * يتصارع utility مع `.nav-link-active` في طبقة المكوّنات، فسقطت `!important`
 * التي كانت تفرض خلفية الرابط النشط ولونه.
 */
const NAV_LINK_CLASS =
  'nav-link group flex w-full items-center overflow-hidden rounded-lg text-caption transition-colors duration-[var(--dur-1)]';
const NAV_LINK_INACTIVE_CLASS = 'text-text-secondary hover:bg-surface-raised hover:text-text';
const NAV_LINK_ACTIVE_CLASS = 'nav-link-active font-semibold';
const NAV_LABEL_CLASS = 'min-w-0 flex-1 truncate';

interface SidebarGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

function sidebarItems(...routes: readonly NavItem['to'][]): readonly NavItem[] {
  return routes.map((route) => {
    const item = NAV_ITEMS.find((candidate) => candidate.to === route);
    if (item === undefined) throw new Error(`مسار القائمة غير مسجّل: ${route}`);
    return item;
  });
}

/** وجهات القائمة كلّها — تُشتقّ من الجدول فلا تنحرف قائمةٌ ثانية عنه. */
const NAV_TARGETS: readonly string[] = NAV_ITEMS.map((item) => item.to);

/**
 * وجهاتٌ في الجدول **خارج القائمة الجانبية عمدًا** — ولكلٍّ موضعُها المعلن.
 *
 * والإعلان صريحٌ لا ضمنيّ: من يضيف وجهةً عليه إمّا أن يضعها في مجموعة أو أن
 * يكتب هنا لماذا لا. وبلا ذلك تمرّ وجهةٌ مُسجَّلة لا يعرضها شيء — وهو ما وقع.
 */
const NAV_OUTSIDE_SIDEBAR: readonly NavItem['to'][] = [
  /* الإعدادات في نهاية الشريط العلوي بطلب المالك — وفي درج الجوال أسفله. */
  '/settings',
];

/**
 * **المجموعات تسرد مساراتها بالاسم** — فعنصرٌ يُضاف إلى `NAV_ITEMS` ولا يُذكر
 * في مجموعةٍ **لا يُعرض أبدًا**، ويبقى مُصرَّفًا ومُسجَّلًا في `NAV_TARGETS`.
 *
 * وقع ذلك حرفيًّا عند إضافة «القصائد» (‏2026-10-20): أُضيفت إلى الجدول ونُشرت
 * ولم تظهر في القائمة، فسأل المالك «وينه مالقيته». و`sidebarItems` يحرس
 * الاتجاه الواحد (مسارٌ في مجموعةٍ ليس في الجدول يرمي)، ولا يحرس عكسه —
 * فيحرسه `navItemsAllGrouped` أدناه ويختبره `nav-groups.test.ts`.
 */
const SIDEBAR_GROUPS: readonly SidebarGroup[] = [
  {
    label: 'مسار العمل',
    items: sidebarItems('/', '/tasks', '/projects'),
  },
  {
    label: 'المتابعة',
    items: sidebarItems('/reminders'),
  },
  {
    label: 'المساحة',
    items: sidebarItems('/personal/notebook', '/personal/workshop', '/personal/poems', '/personal'),
  },
];

/**
 * كل وجهةٍ في الجدول معروضةٌ في مجموعةٍ واحدة، أو مُعلَنٌ خروجها — تُصدَّر للاختبار.
 *
 * والمقارنة بالمجموعات لا بالعدد: عنصران يتبادلان موضعيهما يبقى عددهما سليمًا.
 */
export function navItemsAllGrouped(): {
  readonly missing: readonly string[];
  readonly duplicated: readonly string[];
  /** وجهةٌ أُعلن خروجها ثم وُضعت في مجموعة — إعلانٌ تقادم. */
  readonly staleExclusions: readonly string[];
} {
  const grouped = SIDEBAR_GROUPS.flatMap((group) => group.items.map((item) => item.to));
  const seen = new Set<string>();
  const duplicated = grouped.filter((to) => (seen.has(to) ? true : (seen.add(to), false)));
  const declared = new Set<string>(NAV_OUTSIDE_SIDEBAR);
  return {
    missing: NAV_TARGETS.filter((to) => !seen.has(to) && !declared.has(to)),
    duplicated,
    staleExclusions: [...declared].filter((to) => seen.has(to)),
  };
}

function SideNav({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return (
    <aside
      aria-label="القائمة الجانبية"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={
        'app-sidebar hidden shrink-0 overflow-x-hidden border-e border-border transition-[width] duration-[var(--dur-2)] lg:flex lg:h-dvh lg:flex-col ' +
        (collapsed ? 'w-16' : 'w-[260px]')
      }
    >
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'توسيع القائمة' : 'تصغير القائمة'}
          title={collapsed ? 'توسيع القائمة' : 'تصغير القائمة'}
          className="grid size-9 shrink-0 place-items-center rounded-lg text-text-secondary transition-colors duration-[var(--dur-1)] hover:bg-surface-raised hover:text-text"
        >
          <Menu aria-hidden="true" className="size-[18px]" />
        </button>
        {!collapsed && (
          <span className="flex min-w-0 flex-1 items-center justify-center">
            <BrandName />
          </span>
        )}
      </header>

      <nav
        aria-label="التنقل الرئيسي"
        className="command-nav-scroll min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-2 py-2"
      >
        {SIDEBAR_GROUPS.map((group, groupIndex) => (
          <section
            key={group.label}
            className={groupIndex === 0 ? '' : 'mt-3 pt-3'}
          >
            <h2
              className={
                collapsed
                  ? 'sr-only'
                  : 'flex items-center gap-2 px-2 pt-1 pb-2 text-[10.5px] font-semibold text-text-muted'
              }
            >
              <span className="shrink-0">{group.label}</span>
            </h2>
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={
                      NAV_LINK_CLASS + ' min-h-9' +
                      (collapsed ? ' justify-center px-2' : ' gap-2.5 pe-1 ps-2') + ' ' +
                      (isNavActive(pathname, item.to, NAV_TARGETS)
                        ? NAV_LINK_ACTIVE_CLASS
                        : NAV_LINK_INACTIVE_CLASS)
                    }
                    aria-current={isNavActive(pathname, item.to, NAV_TARGETS) ? 'page' : undefined}
                    title={item.label}
                  >
                    <item.icon aria-hidden="true" className="size-[18px] shrink-0" />
                    {!collapsed && <span className={NAV_LABEL_CLASS}>{item.label}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <VenturesNav collapsed={collapsed} />
      </nav>
    </aside>
  );
}

function VenturesNav({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const ventures = useQuery({
    queryKey: qk.ventures(),
    queryFn: () => api.get<Page<VentureDto>>('/ventures'),
    staleTime: 5 * 60_000,
  });

  const items = (ventures.data?.items ?? []).filter((venture) => venture.status !== 'archived');
  if (items.length === 0) return null;

  return (
    <section className="mt-3 pt-3">
      <h2
        className={
          collapsed
            ? 'sr-only'
            : 'flex items-center gap-2 px-2 pt-1 pb-2 text-[10.5px] font-semibold text-text-muted'
        }
      >
        <span className="shrink-0">المظلات</span>
      </h2>
      <ul className="flex flex-col gap-0.5">
        {items.map((venture) => (
          <li key={venture.id}>
            <Link
              to="/ventures/$slug"
              params={{ slug: venture.slug }}
              onClick={onNavigate}
              className={
                NAV_LINK_CLASS + (onNavigate === undefined ? ' min-h-9' : ' min-h-11') +
                (collapsed ? ' justify-center px-2' : ' gap-2.5 pe-1 ps-2')
              }
              activeProps={{ className: NAV_LINK_ACTIVE_CLASS, 'aria-current': 'page' }}
              inactiveProps={{ className: NAV_LINK_INACTIVE_CLASS }}
              title={venture.name}
            >
              <span
                aria-hidden="true"
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: ventureColor(venture.color) }}
              />
              {!collapsed && <UserSpan className={NAV_LABEL_CLASS}>{venture.name}</UserSpan>}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ==================== TopBar ==================== */

/**
 * زرّ أيقونة الشريط العلوي — **بقياسات bestversion المقروءة من مصدره**
 * (`components/shell/top-bar.tsx` و`sidebar-icon.tsx`):
 *
 *  · الهدف اللمسيّ `size-11` (44px) — يبقى كما هو، فهو قاعدة اللمس العالمية.
 *  · **الأيقونة نفسها 18px بسمك 2** — وهذا ما كان مختلفًا: لوسيد الافتراضي
 *    24px، فكانت أيقونات فلو أكبر بثلث من المرجع داخل هدفٍ بالحجم نفسه.
 *    والمالك رآها «أكبر من كذا» ومحقّ — الفرق ثلث.
 *  · نصف القطر 8px (`rounded-btn` عند المرجع) لا 6px.
 *  · و`active:scale-95` — استجابةُ ضغطٍ يشعر بها المرجع ولم تكن هنا.
 *
 * ويبقى `.top-action` (الحدّ الشفاف الذي يظهر عند التمرير) لأنه هوية Woblo لا
 * bestversion — والاثنان لا يتعارضان.
 */
const ICON_BUTTON_CLASS =
  'grid size-11 shrink-0 place-items-center rounded-lg text-text-secondary transition-[color,background-color,border-color,transform] duration-[var(--dur-1)] hover:text-text active:scale-95 [&_svg]:size-[18px] [&_svg]:stroke-2';

function TopBar({
  online,
  isToday,
  onCapture,
  onOpenMenu,
  menuOpen,
  menuTriggerRef,
}: {
  online: boolean;
  isToday: boolean;
  onCapture: () => void;
  onOpenMenu: () => void;
  menuOpen: boolean;
  menuTriggerRef: { readonly current: HTMLButtonElement | null };
}) {
  const live = liveLabel(online, useActivityStreamStatus());
  /* نفس مفتاح أجندة الشاشة: TanStack يدمج الطلبين، ويُعطَّل كليًّا خارج «اليوم». */
  const agenda = useAgendaQuery(isToday);
  /**
   * **التاريخ في كل شاشة لا في «اليوم» وحدها** — نصّ المالك في مرفقه: «لاحظ
   * في **أعلى الموقع**… وفيها تاريخ هجري وميلادي». لا «أعلى الرئيسية».
   *
   * وكان مربوطًا بـ`agenda.data.date` وهي معطَّلة خارج `/`، فيختفي التاريخان
   * معًا على كل شاشةٍ أخرى. والمرجع يحسبه من ساعة الجهاز عند التركيب
   * (`top-bar.tsx`: `clock` من `new Date()`) بلا نداءٍ ولا اعتمادٍ على مسار.
   *
   * **ولا نداء ثانٍ هنا كذلك**: يُقرأ من الأجندة حين تكون محمَّلة (فهي أدقّ —
   * يوم المالك بمنطقته من الخادم)، ويسقط إلى ساعة الجهاز حين لا تكون. والفرق
   * بينهما يقع في ساعاتٍ قليلة حول منتصف الليل فقط، والتاريخ في الشريط
   * **مرافقةٌ بصرية لا مقياس** — والمقياس في الأجندة نفسها.
   */
  const todayDate = agenda.data?.date ?? dayKey(new Date());
  return (
    <header className="app-topbar sticky top-0 z-[var(--z-sticky)] flex items-center gap-1 px-2 lg:px-6">
      <span className="flex min-w-0 items-center gap-1 lg:hidden">
        <button
          ref={menuTriggerRef}
          type="button"
          onClick={onOpenMenu}
          aria-label="فتح القائمة"
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
          className={`${ICON_BUTTON_CLASS} top-action`}
        >
          <Menu aria-hidden="true" />
        </button>
        {isToday ? (
          <TodayBarIdentity date={todayDate} compact />
        ) : (
          <span className="flex min-w-0 items-center px-1">
            <BrandName />
          </span>
        )}
      </span>

      {/*
       * **الهوية والتاريخان في كل شاشة** — لا في «اليوم» وحدها. وكان الفرع
       * الآخر يعرض «إشارة النظام / مباشرة» بلا تاريخ، فيختفي ما طلبه المالك
       * («لاحظ في **أعلى الموقع**… وفيها تاريخ هجري وميلادي») على كل صفحةٍ
       * سوى الرئيسية. والمرجع يعرضه في كل شاشة (`top-bar.tsx`).
       *
       * وحالة البثّ لم تسقط: `TodayBarIdentity` تحمل «الإشارة الآن» ونبضتها،
       * ونصُّها هنا يصير `live.text` حين يكون فيه ما يُقال (تحذير) — فلا
       * يخسر المالك معلومةً بحصول الأخرى.
       */}
      <TodayBarIdentity date={todayDate} live={live} />

      <div className="flex-1" />

      {!online && (
        <span className="flex items-center gap-1 text-caption text-warning" title="غير متصل">
          <WifiOff aria-hidden="true" className="size-4" />
          <span className="sr-only">غير متصل</span>
        </span>
      )}

      {/*
        القيد السريع أيقونةً في الشريط العلوي بطلب المالك — نُقل من الشريط
        الجانبي حيث كان زرًّا عريضًا بنصّ واختصار.

        والاختصار لم يُحذف بل انتقل إلى `title`/`aria-label`: من يعرفه يستعمله،
        ومن لا يعرفه لا يحتاج سطرًا يشغل عرض الشريط الجانبي كل يوم.
      */}
      <button
        type="button"
        onClick={onCapture}
        aria-label={`قيد سريع (${CAPTURE_SHORTCUT_LABEL})`}
        title={`قيد سريع · ${CAPTURE_SHORTCUT_LABEL}`}
        className={`${ICON_BUTTON_CLASS} top-action`}
      >
        <PenLine aria-hidden="true" />
      </button>
      <Link to="/search" aria-label="البحث" className={`${ICON_BUTTON_CLASS} top-action`}>
        <Search aria-hidden="true" />
      </Link>
      {/*
        الإعدادات في **نهاية الشريط العلوي** (يسارًا في RTL) بطلب المالك —
        وهو موضعها في bestversion حرفيًّا (`top-bar.tsx`: رابط `/settings` آخر
        عنصر في المجموعة الطرفية). وكانت في بطاقة المالك أسفل الشريط الجانبي،
        وقد حُذفت البطاقة كلها بطلبه.
      */}
      <Link to="/settings" aria-label="الإعدادات" title="الإعدادات" className={`${ICON_BUTTON_CLASS} top-action`}>
        <Settings aria-hidden="true" />
      </Link>
    </header>
  );
}

/**
 * هوية «اليوم» تعيش في الكروم وحده: كانت التحية والتاريخ وإشارة اللحظة تعيد
 * افتتاح الصفحة داخل المحتوى، فتدفع أول قرار إلى أسفل بلا أن تضيف معلومة.
 * النسخة المدمجة تحافظ على الأفعال الأربعة عند 320px؛ التفاصيل تظهر من 360px.
 */
function TodayBarIdentity({
  date,
  compact = false,
  live,
}: {
  date: string | null;
  compact?: boolean;
  /** حال البثّ — يُعرض بدل «الإشارة الآن» متى كان فيه تحذير. */
  live?: { readonly text: string; readonly warn: boolean };
}) {
  if (compact) {
    return (
      <span
        data-testid="today-topbar-identity-mobile"
        className="flex min-w-0 flex-1 flex-col overflow-hidden px-1 leading-tight lg:hidden"
      >
        {/*
          والجوال 12px لا 11/10: قِيس المصدر `text-xs` (‏12px) تحت `md`. وأحجامٌ
          مخترَعة بالقوس (`text-[11px]`) تخرج من سلّم الرموز أصلًا — فلا تتبع
          تكبير المتصفّح ولا تُراجَع مع بقية السلّم.
        */}
        <span className="flex min-w-0 items-center gap-1 text-micro text-text-secondary">
          <span aria-hidden="true" className="command-live-pulse shrink-0" />
          <span className="min-w-0 truncate min-[360px]:shrink-0">الإشارة الآن</span>
          <span className="hidden min-w-0 truncate font-semibold text-text min-[360px]:inline">
            {greeting()}
          </span>
        </span>
        {date !== null && (
          <span
            data-testid="today-topbar-date-mobile"
            className="hidden truncate text-micro text-text-muted min-[360px]:block"
          >
            {formatDayHeading(date)}
          </span>
        )}
      </span>
    );
  }

  return (
    <span
      data-testid="today-topbar-identity"
      className="hidden min-w-0 items-center gap-3 text-text-secondary lg:flex"
    >
      <span className="flex shrink-0 items-center gap-2 text-caption">
        <span aria-hidden="true" className="command-live-pulse" />
        <span className={live?.warn === true ? 'text-warning' : undefined}>
          {live?.warn === true ? live.text : 'الإشارة الآن'}
        </span>
      </span>
      <span aria-hidden="true" className="h-4 w-px bg-border" />
      {/*
        **التحية 14px متوسّطة لا 18px عريضة** — بلاغ المالك 2026-08-09: «لاحظ
        في أعلى الموقع… حجم الخط فيها **أفضل من حقي**».

        وقِيس المصدر: شريطه كلّه `text-xs md:text-sm` (‏12/14px) والتحية فيه
        `font-medium` — أي **14px متوسّطة**. وكانت عندنا `text-heading` (‏18px)
        بوزن `strong`، فتقفز عن جارتيها في السطر نفسه (‏«الإشارة الآن» والتاريخ
        كلتاهما 14px) — وقفزةُ أربع نقاط ووزنٍ كامل في شريطٍ ارتفاعه 56px هي ما
        يجعله يُقرأ صاخبًا.
      */}
      <span className="shrink-0 text-caption font-semibold text-text">{greeting()}</span>
      {date !== null && (
        <>
          <span data-testid="today-topbar-date" className="truncate text-caption text-text-muted">
            {formatGregorianFull(date)}
          </span>
          {/*
            **التاريخ الهجريّ — هنا وحده** (أمر المالك 2026-08-09: «الهجري فقط
            للعرض في هذا المكان ولا تقم بوضعه في أي مكان أخر»). فهو مرافقةٌ
            بصرية لا مقياس: المعتمد في فلو كلّه الميلاديّ، ولذلك يأتي **بعده**
            وبلونٍ أخفت، ويسقط أولًا عند ضيق الشريط (`xl:inline`).
          */}
          <span aria-hidden="true" className="inline-block h-3 w-px bg-border" />
          <span
            data-testid="today-topbar-hijri"
            title="التاريخ الهجري"
            className="shrink-0 text-caption text-text-muted/80"
          >
            {formatHijriDisplay(date)}
          </span>
        </>
      )}
    </span>
  );
}

/* ==================== شرائط الحالة ==================== */

/** نص §4.3 حرفيًا: باقي الشاشات تعرض آخر كاش مع هذا الشريط. */
function OfflineBar() {
  return (
    <p
      role="status"
      className="border-b border-border bg-surface-raised px-4 py-1.5 text-center text-caption text-text-secondary"
    >
      غير متصل — البيانات قد لا تكون محدّثة
    </p>
  );
}

/**
 * §4.2: `registerType: 'prompt'` — لا تحديث قسري يقطع الكتابة. الشريط يظهر
 * وينتظر لمسة المالك.
 */
function UpdateBar() {
  const { needRefresh, updateServiceWorker } = useRegisterSW();
  const [waiting, setWaiting] = needRefresh;

  return (
    <>
      {waiting && (
        <div
          aria-live="polite"
          className="animate-banner-in flex items-center justify-center gap-3 border-b border-border bg-accent-subtle px-4 py-2 text-caption text-text"
        >
          <span>نسخة جديدة —</span>
          <button
            type="button"
            onClick={() => {
              setWaiting(false);
              void updateServiceWorker(true);
            }}
            className="flex items-center gap-1 rounded-sm px-2 py-1 text-caption font-semibold text-accent hover:bg-surface-raised"
          >
            <RefreshCw aria-hidden="true" className="size-4" />
            تحديث
          </button>
        </div>
      )}
    </>
  );
}

/* ==================== الجوال: رأس واحد ودرج جانبي ==================== */

/**
 * نفس نموذج BestVersion المقروء من مصدره: hamburger في الرأس، ودرج 260px من
 * الحافة البادئة (يمين RTL). `<dialog>` يحصر التركيز ويغلق Escape، والزر الخلفي
 * يغلق باللمس؛ فلا نعيد يدويًّا أخطاء focus trap أو body scroll lock.
 */
function MobileNavDrawer({
  open,
  onClose,
  onCapture,
}: {
  open: boolean;
  onClose: () => void;
  onCapture: () => void;
}) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const captureAfterCloseRef = useRef(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;
    let focusFrame: number | undefined;
    if (open && !dialog.open) {
      dialog.showModal();
      focusFrame = requestAnimationFrame(() => closeButtonRef.current?.focus());
    }
    if (!open && dialog.open) dialog.close();
    return () => {
      if (focusFrame !== undefined) cancelAnimationFrame(focusFrame);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const desktop = window.matchMedia('(min-width: 1024px)');
    const closeAtDesktop = (event: MediaQueryListEvent): void => {
      if (event.matches) onClose();
    };
    desktop.addEventListener('change', closeAtDesktop);
    return () => desktop.removeEventListener('change', closeAtDesktop);
  }, [onClose, open]);

  return (
    <dialog
      ref={dialogRef}
      onClose={() => {
        if (captureAfterCloseRef.current) {
          captureAfterCloseRef.current = false;
          onCapture();
          return;
        }
        onClose();
      }}
      aria-labelledby="mobile-nav-title"
      className="fixed inset-0 m-0 h-dvh max-h-none w-full max-w-none bg-transparent p-0 text-text backdrop:bg-transparent lg:hidden"
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label="إغلاق القائمة"
        onClick={onClose}
        className="fixed inset-0 bg-black/50"
      />

      <aside
        aria-label="القائمة الجانبية للجوال"
        className="app-sidebar fixed inset-y-0 start-0 z-[var(--z-modal)] flex h-dvh w-[260px] max-w-[86vw] flex-col border-e border-border bg-surface-raised shadow-2"
      >
        <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-3">
          {/* والجوال يعرض الاسم نفسه ولا يحرّره: الترويسة الضيّقة ليست موضع تحرير. */}
          <h2 id="mobile-nav-title" className="flex min-w-0 flex-1 items-center">
            <BrandName />
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            autoFocus
            onClick={onClose}
            aria-label="إغلاق القائمة"
            className={`${ICON_BUTTON_CLASS} top-action`}
          >
            <X aria-hidden="true" />
          </button>
        </header>

        <button
          type="button"
          onClick={() => {
            captureAfterCloseRef.current = true;
            dialogRef.current?.close();
          }}
          className="primary-action mx-3 mt-3 min-h-11 justify-center rounded-lg text-caption"
        >
          <PenLine aria-hidden="true" className="size-4" />
          قيد سريع
        </button>

        <nav
          aria-label="التنقل الرئيسي للجوال"
          className="command-nav-scroll min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-2 py-2"
        >
          {SIDEBAR_GROUPS.map((group, groupIndex) => (
            <section
              key={group.label}
              className={groupIndex === 0 ? '' : 'mt-3 pt-3'}
            >
              <h3 className="flex items-center gap-2 px-2 pt-1 pb-2 text-[10.5px] font-semibold text-text-muted">
                <span className="shrink-0">{group.label}</span>
              </h3>
              <ul className="flex flex-col gap-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={onClose}
                      className={`${NAV_LINK_CLASS} min-h-11 gap-2.5 pe-1 ps-2 ${
                        isNavActive(pathname, item.to, NAV_TARGETS)
                          ? NAV_LINK_ACTIVE_CLASS
                          : NAV_LINK_INACTIVE_CLASS
                      }`}
                      aria-current={isNavActive(pathname, item.to, NAV_TARGETS) ? 'page' : undefined}
                    >
                      <item.icon aria-hidden="true" className="size-[18px] shrink-0" />
                      <span className={NAV_LABEL_CLASS}>{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <VenturesNav collapsed={false} onNavigate={onClose} />

          <section className="mt-3 pt-3">
            <h3 className="flex items-center gap-2 px-2 pt-1 pb-2 text-[10.5px] font-semibold text-text-muted">
              <span className="shrink-0">النظام</span>
            </h3>
            <Link
              to="/settings"
              onClick={onClose}
              className={`${NAV_LINK_CLASS} min-h-11 gap-2.5 pe-1 ps-2`}
              activeProps={{ className: NAV_LINK_ACTIVE_CLASS, 'aria-current': 'page' }}
              inactiveProps={{ className: NAV_LINK_INACTIVE_CLASS }}
            >
              <Settings aria-hidden="true" className="size-[18px] shrink-0" />
              <span className={NAV_LABEL_CLASS}>الإعدادات</span>
            </Link>
          </section>
        </nav>
      </aside>
    </dialog>
  );
}

/* ==================== حالة الاتصال ==================== */

function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() => navigator.onLine);

  useEffect(() => {
    const goOnline = (): void => setOnline(true);
    const goOffline = (): void => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}
